import React, { useState, useEffect } from 'react';
import SideMenu from './SideMenu';
import UserManagement from './UserManagement';
import AdminManagement from './AdminManagement';
import SubscriptionManagement from './SubscriptionManagement';
import UserSubscriptions from './UserSubscriptions';
import ProductManagement from './ProductManagement';
import ProductFeatureManagement from './ProductFeatureManagement';
import AffiliateMemberManagement from './AffiliateMemberManagement';
import AffiliateMemberViewPage from './AffiliateMemberViewPage';
import AffiliateProgramManagement from './AffiliateProgramManagement';
import AffiliateCommissionManagement from './AffiliateCommissionManagement';
import PromoCodeManagement from './PromoCodeManagement';
import PaymentProviderManagement from './PaymentProviderManagement';
import SubscriptionOrderManagement from './SubscriptionOrderManagement';
import { ArrowUpIcon, ArrowDownIcon, ShoppingBagIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { apiService } from '../services/api';

interface DashboardProps {
  user: any;
  onLogout: () => void;
}

const COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8884D8'];

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [viewMemberId, setViewMemberId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState('7'); // days

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<{
    PENDING?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    PAID?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    FAILED?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    REFUNDED?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
    EXPIRED?: { 
      count: number; 
      totalSubtotalAmount: number;
      countPercentage: number;
      amountPercentage: number;
      description: string;
    };
  }>({});
  const [productsData, setProductsData] = useState<{
    [key: string]: {
      productName: string;
      productSlug: string;
      total: number;
    };
  }>({});
  const [userSubscriptionByProduct, setUserSubscriptionByProduct] = useState<{
    [key: string]: number;
  }>({});
  const [userAnalytics, setUserAnalytics] = useState<{
    totalUserPeriod: number;
    totalUserPeriodPercentage: number;
    description: string;
  } | null>(null);
  const [userChartData, setUserChartData] = useState<Array<{
    date?: string;
    month?: string;
    users: number;
    newUsers: number;
  }>>([]);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setActiveSection(hash);
    }
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateRange));

        const startDateISO = startDate.toISOString();
        const endDateISO = endDate.toISOString();

        const [statisticsResponse, productsResponse, userSubscriptionResponse, userAnalyticsResponse, userChartResponse] = await Promise.all([
          apiService.getOrderStatisticsByStatus(
            startDateISO,
            endDateISO
          ),
          apiService.getTotalSubscriptionsByProduct(
            startDateISO,
            endDateISO
          ),
          apiService.getProductCountByUser(
            startDateISO,
            endDateISO
          ),
          apiService.getUserCounts(
            startDateISO,
            endDateISO
          ),
          apiService.getUserChartData(
            startDateISO,
            endDateISO
          ),
        ]);

        setStatistics(statisticsResponse || {});
        setProductsData(productsResponse || {});
        setUserSubscriptionByProduct(userSubscriptionResponse || {});
        setUserAnalytics(userAnalyticsResponse || null);
        setUserChartData(userChartResponse || []);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dateRange]);

  const toggleSideMenu = () => {
    setIsSideMenuOpen(!isSideMenuOpen);
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    window.location.hash = section;
  };

  const renderDashboard = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading dashboard data...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">Error: {error}</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Date Filter */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Date Filter</h3>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>

        {/* Stats Cards - 3 columns 2 rows */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* 1. Total Users */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{userAnalytics?.totalUserPeriod || 0}</p>
                <div className="flex items-center mt-2">
                  {userAnalytics?.totalUserPeriodPercentage !== undefined && userAnalytics.totalUserPeriodPercentage !== 0 ? (
                    <>
                      {userAnalytics.totalUserPeriodPercentage > 0 ? (
                        <ArrowUpIcon className="text-green-500 mr-1 w-4 h-4" />
                      ) : (
                        <ArrowDownIcon className="text-red-500 mr-1 w-4 h-4" />
                      )}
                      <span className={`text-sm ${userAnalytics.totalUserPeriodPercentage > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {userAnalytics.totalUserPeriodPercentage > 0 ? '+' : ''}{userAnalytics.totalUserPeriodPercentage.toFixed(1)}%
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {userAnalytics.description || 'from last period'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {userAnalytics?.description || 'from last period'}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ShoppingBagIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* 2. Pending*/}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.PENDING?.count || 0}</p>
                <div className="flex items-center mt-2">
                  {statistics.PENDING?.countPercentage !== undefined && statistics.PENDING.countPercentage !== 0 ? (
                    <>
                      {statistics.PENDING.countPercentage > 0 ? (
                        <ArrowUpIcon className="text-green-500 mr-1 w-4 h-4" />
                      ) : (
                        <ArrowDownIcon className="text-red-500 mr-1 w-4 h-4" />
                      )}
                      <span className={`text-sm ${statistics.PENDING.countPercentage > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {statistics.PENDING.countPercentage > 0 ? '+' : ''}{statistics.PENDING.countPercentage.toFixed(1)}%
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {statistics.PENDING?.description || 'from last period'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {statistics.PENDING?.description || 'from last period'}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <ShoppingBagIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* 3. Paid (PAID orders total amount) */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Paid</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statistics.PAID?.totalSubtotalAmount 
                    ? statistics.PAID.totalSubtotalAmount >= 1000000 
                      ? `Rp ${(statistics.PAID.totalSubtotalAmount / 1000000).toFixed(2)}M`
                      : `Rp ${(statistics.PAID.totalSubtotalAmount / 1000).toFixed(0)}K`
                    : 'Rp 0'}
                </p>
                <div className="flex items-center mt-2">
                  {statistics.PAID?.amountPercentage !== undefined && statistics.PAID.amountPercentage !== 0 ? (
                    <>
                      {statistics.PAID.amountPercentage > 0 ? (
                        <ArrowUpIcon className="text-green-500 mr-1 w-4 h-4" />
                      ) : (
                        <ArrowDownIcon className="text-red-500 mr-1 w-4 h-4" />
                      )}
                      <span className={`text-sm ${statistics.PAID.amountPercentage > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {statistics.PAID.amountPercentage > 0 ? '+' : ''}{statistics.PAID.amountPercentage.toFixed(1)}%
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {statistics.PAID?.description || 'from last period'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {statistics.PAID?.description || 'from last period'}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>

          {/* 4. Failed (PENDING orders count) */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.FAILED?.count || 0}</p>
                <div className="flex items-center mt-2">
                  {statistics.FAILED?.countPercentage !== undefined && statistics.FAILED.countPercentage !== 0 ? (
                    <>
                      {statistics.FAILED.countPercentage > 0 ? (
                        <ArrowUpIcon className="text-red-500 mr-1 w-4 h-4" />
                      ) : (
                        <ArrowDownIcon className="text-green-500 mr-1 w-4 h-4" />
                      )}
                      <span className={`text-sm ${statistics.FAILED.countPercentage > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {statistics.FAILED.countPercentage > 0 ? '+' : ''}{statistics.FAILED.countPercentage.toFixed(1)}%
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {statistics.FAILED?.description || 'from last period'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {statistics.FAILED?.description || 'from last period'}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <ShoppingBagIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* 5. Refunded Orders */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Refunded</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.REFUNDED?.count || 0}</p>
                <div className="flex items-center mt-2">
                  {statistics.REFUNDED?.countPercentage !== undefined && statistics.REFUNDED.countPercentage !== 0 ? (
                    <>
                      {statistics.REFUNDED.countPercentage > 0 ? (
                        <ArrowUpIcon className="text-red-500 mr-1 w-4 h-4" />
                      ) : (
                        <ArrowDownIcon className="text-green-500 mr-1 w-4 h-4" />
                      )}
                      <span className={`text-sm ${statistics.REFUNDED.countPercentage > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {statistics.REFUNDED.countPercentage > 0 ? '+' : ''}{statistics.REFUNDED.countPercentage.toFixed(1)}%
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {statistics.REFUNDED?.description || 'from last period'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {statistics.REFUNDED?.description || 'from last period'}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <ShoppingBagIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          {/* 6. Expired Orders */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Expired</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.EXPIRED?.count || 0}</p>
                <div className="flex items-center mt-2">
                  {statistics.EXPIRED?.countPercentage !== undefined && statistics.EXPIRED.countPercentage !== 0 ? (
                    <>
                      {statistics.EXPIRED.countPercentage > 0 ? (
                        <ArrowUpIcon className="text-red-500 mr-1 w-4 h-4" />
                      ) : (
                        <ArrowDownIcon className="text-green-500 mr-1 w-4 h-4" />
                      )}
                      <span className={`text-sm ${statistics.EXPIRED.countPercentage > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {statistics.EXPIRED.countPercentage > 0 ? '+' : ''}{statistics.EXPIRED.countPercentage.toFixed(1)}%
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {statistics.EXPIRED?.description || 'from last period'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {statistics.EXPIRED?.description || 'from last period'}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-3 bg-gray-100 rounded-full">
                <ShoppingBagIcon className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Growth Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
            {userChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={userChartData.map(item => ({
                  date: item.date || item.month || '',
                  users: item.users,
                  newUsers: item.newUsers
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="users" stroke="#064E3A" strokeWidth={2} />
                  <Line type="monotone" dataKey="newUsers" stroke="#82ca9d" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">Loading user chart data...</p>
              </div>
            )}
          </div>

          {/* Total Subscription By Product Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Subscription By Product</h3>
            {Object.keys(productsData).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={Object.entries(productsData).map(([slug, data]) => ({
                  product: data.productName,
                  subscriptions: data.total
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="product" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="subscriptions" stroke="#064E3A" fill="#C5E6DD" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">No subscription data available</p>
              </div>
            )}
          </div>
        </div>

        {/* User Subscription by Product Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Subscription by Product</h3>
          {Object.keys(userSubscriptionByProduct).length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(userSubscriptionByProduct).map(([slug, count]) => ({
                      name: slug.charAt(0).toUpperCase() + slug.slice(1),
                      value: count,
                      slug: slug
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent, payload }) => `${payload?.name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(userSubscriptionByProduct).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-4">
                {Object.entries(userSubscriptionByProduct).map(([slug, count], index) => (
                  <div key={slug} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                      <span className="font-medium">{slug.charAt(0).toUpperCase() + slug.slice(1)}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{count} users</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">No user subscription data available</p>
            </div>
          )}
        </div>

        {/* Data Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Klasmen Table */}

          {/* Jadwal Pertandingan Table */}

        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'user-management':
        return (
          <UserManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'admin-management':
        return (
          <AdminManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'subscription-management':
        return (
          <SubscriptionManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'user-subscriptions':
        return (
          <UserSubscriptions onBack={() => setActiveSection('dashboard')} />
        );
      case 'product-management':
        return (
          <ProductManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'product-feature-management':
        return (
          <ProductFeatureManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'affiliate-member-management':
        return (
          <AffiliateMemberManagement 
            onBack={() => setActiveSection('dashboard')}
            onViewMember={(memberId) => {
              setViewMemberId(memberId);
              setActiveSection('affiliate-member-view');
            }}
          />
        );
      case 'affiliate-member-view':
        return (
          <AffiliateMemberViewPage
            memberId={viewMemberId || 0}
            onBack={() => {
              setViewMemberId(null);
              setActiveSection('affiliate-member-management');
            }}
          />
        );
      case 'affiliate-program-management':
        return (
          <AffiliateProgramManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'affiliate-commission-management':
        return (
          <AffiliateCommissionManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'promo-code-management':
        return (
          <PromoCodeManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'payment-provider-management':
        return (
          <PaymentProviderManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'subscription-order-management':
        return (
          <SubscriptionOrderManagement onBack={() => setActiveSection('dashboard')} />
        );
      case 'ketersediaan':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ticket Availability</h2>
            <p className="text-gray-600">Manage ticket availability and seating.</p>
          </div>
        );
      case 'pembelian':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ticket Purchases</h2>
            <p className="text-gray-600">View and manage ticket purchase history.</p>
          </div>
        );
      case 'merchandise-list':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Merchandise Management</h2>
            <p className="text-gray-600">Manage club merchandise and products.</p>
          </div>
        );
      case 'merchandise-pembelian':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Merchandise Purchases</h2>
            <p className="text-gray-600">View and manage merchandise purchase history.</p>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Side Menu */}
      <SideMenu
        isOpen={isSideMenuOpen}
        onToggle={toggleSideMenu}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
      />

      {/* Main Content */}
      <div className={`transition-all duration-300 ${isSideMenuOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <header className="bg-white shadow">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center">
              <button
                onClick={toggleSideMenu}
                className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 mr-3"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {user.name}</span>
              <button
                onClick={onLogout}
                className="bg-imola-primary hover:bg-imola-primary/80 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard; 